var W3CWebSocket = require("websocket").w3cwebsocket;
import eventEmitter from "./utils/eventEmitter";

class SocketClient {
	#ws = null;
	static instance = null;
	heartbeatInterval = null;
	reconnectInProgress = false;

	constructor(tenantId, contactId, botId) {
		if (SocketClient.instance) return SocketClient.instance;

		// contactId is the raw ID (sessionId or uuid) - socket URL appends _botId
		this.tenantId = tenantId || localStorage.getItem("tenantId");
		this.contactId = contactId || localStorage.getItem("contactId");
		this.botId = botId || localStorage.getItem("botId");

		if (!this.tenantId || !this.contactId || !this.botId) {
			console.error("@@ WWS @@ Missing required params to initialize socket.");
			return;
		}

		this._lastHeartbeat = Date.now();
		this._hasConnectedBefore = false;

		SocketClient.instance = this;
		this.initializeSocket();
		this.startSleepWatcher();
		this.handleVisibilityChange();
		this.handleOnlineChange();
	}

	initializeSocket() {
		try {
			this.#ws = new W3CWebSocket(
				`${process.env.REACT_APP_WEBSOCKET_URL || "ws://localhost:8000"}?origin=wws&tenantId=${this.tenantId}&contactId=${this.contactId}_${this.botId}`
			);

			this.setEventListeners();

		// Heartbeat: send ping every 15s (matching server interval) and
		// check that we've heard from the server within the last 40s.
		this._lastHeartbeat = Date.now();
		this.heartbeatInterval = setInterval(() => {
			if (!this.#ws || this.#ws.readyState !== this.#ws.OPEN) return;

			// Server pings every 15s. If we haven't heard from it in 40s, the
			// connection is a zombie (TCP open, but no traffic getting through).
			if (Date.now() - this._lastHeartbeat > 40000) {
				console.warn("@@ WWS @@ No server heartbeat in 40s, forcing reconnect");
				this.cleanupListeners();
				this.#ws.close();
				this.reconnect();
				return;
			}

			this.#ws.send(JSON.stringify({ topic: "ping" }));
		}, 15000);
		} catch (error) {
			console.error("@@ WWS @@ socket exception", error);
		}
	}

	setEventListeners() {
		this.#ws.onopen = () => {
			const isReconnect = this._hasConnectedBefore;
			this._hasConnectedBefore = true;
			console.warn("@@ WWS @@ WebSocket connection established", isReconnect ? "(reconnect)" : "(initial)");
			if (isReconnect) {
				eventEmitter.emit("socket_reconnected");
			}
		};

		this.#ws.onclose = (event) => {
			this.cleanupListeners();
			if (event.reason === 'removing_old_connection') {
				console.warn("@@ WWS @@ Removing old connection without reconnect");
				return;
			}
			console.warn("@@ WWS @@ socket closed, reconnecting", event);
			this.reconnect();
		};

		this.#ws.onerror = (error) => {
			console.error('@@ WWS @@ socket error:', error.message);
			this.cleanupListeners();
			// Do not call reconnect here, onclose will handle it
		};

		this.#ws.onmessage = (event) => {
			try {
				const { topic, message } = JSON.parse(event.data);
				switch (topic) {
					case "ping":
						this._lastHeartbeat = Date.now();
						this.#ws.send(JSON.stringify({
							topic: "pong",
							message: { tenantId: this.tenantId, contactId: this.contactId }
						}));
						eventEmitter.emit("agents_update", { ...message });
						break;
					case "record_update_stream":
						eventEmitter.emit(topic, { ...message, _source: "ws" });
						break;
				case "record_update":
				case "assistant_typing":
				case "ticket_created":
				case "contact_update":
				case "conversation_assignee":
				case "conversation_status_update":
					eventEmitter.emit(topic, { ...message });
					break;
				default:
					console.warn("@@ WWS @@ Unhandled socket topic:", topic);
				}
			} catch (error) {
				console.error("@@ Error: onmessage @@", error);
			}
		};
	}

	cleanupListeners() {
		if (this.#ws) {
			this.#ws.onopen = null;
			this.#ws.onclose = null;
			this.#ws.onerror = null;
			this.#ws.onmessage = null;
		}
		clearInterval(this.heartbeatInterval);
	}

	reconnect() {
		if (this.reconnectInProgress) return;

		this.reconnectInProgress = true;
		if (this.#ws) {
			this.#ws.close();
		}

		setTimeout(() => {
			console.warn("@@ WWS @@ Reconnecting to socket...");
			this.initializeSocket();
			this.reconnectInProgress = false;
		}, 2000);
	}

	startSleepWatcher() {
		let lastCheck = Date.now();
		setInterval(() => {
			const now = Date.now();
			if (now - lastCheck > 7000) {
				console.warn("@@ WWS @@ Detected wake from sleep. Reconnecting...");
				this.reconnect();
			}
			lastCheck = now;
		}, 5000);
	}

	handleVisibilityChange() {
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'visible') {
				if (!this.#ws || this.#ws.readyState !== this.#ws.OPEN) {
					console.warn("@@ WWS @@ Tab visible again, reconnecting...");
					this.reconnect();
				}
			}
		});
	}

	handleOnlineChange() {
		window.addEventListener('online', () => {
			console.warn("@@ WWS @@ Network restored, forcing reconnect");
			this.cleanupListeners();
			if (this.#ws) this.#ws.close();
			this.reconnect();
		});
	}

	get client() {
		return this.#ws;
	}

	/**
	 * Destroys the socket connection and cleans up.
	 */
	destroy() {
		this.cleanupListeners();
		if (this.#ws) {
			this.#ws.close();
		}
	}

	/**
	 * Destroys the current socket instance to allow reconnection with new credentials.
	 */
	static destroyInstance() {
		if (SocketClient.instance) {
			SocketClient.instance.destroy();
			SocketClient.instance = null;
		}
	}

	static getInstance(tenantId, contactId, botId) {
		if (!SocketClient.instance) {
			SocketClient.instance = new SocketClient(tenantId, contactId, botId);
		}
		return SocketClient.instance;
	}
}

export default SocketClient;
